import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Fade,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { horsesAPI, referenceAPI, horseRegistrationsAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { formatHorseCurrentStatus } from '../utils/horseStatus';
import { BRAND, cardSx } from '../theme';
import HorseRegistrations from '../components/HorseRegistrations';
import HorseBreedingHistory from '../components/HorseBreedingHistory';
import HorseVetRecords from '../components/HorseVetRecords';
import HorseFeedSchedule from '../components/HorseFeedSchedule';
import HorseShowRecords from '../components/HorseShowRecords';
import HorsePayments from '../components/HorsePayments';
import PedigreeModal from '../components/PedigreeModal';
import HorseTypeBadge from '../components/HorseTypeBadge';
import { useHorseModal } from '../context/HorseModalContext';
import { toArray } from '../utils/apiData';
import { DetailPageSkeleton } from '../components/LoadingSkeletons';

const BREEDING_OWNER_TYPE_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'leased', label: 'Leased' },
  { value: 'client', label: 'Client' },
];

// Label/value pair styled to match the app's table-header language (uppercase,
// letter-spaced, muted brown) so field labels read consistently across pages.
const Field = ({ label, children, sm = 6 }) => (
  <Grid item xs={12} sm={sm}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '0.72rem',
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography variant="body1">{children}</Typography>
  </Grid>
);

// Registration orgs surfaced as a compact status row in the hero header —
// mirrors the org set tracked in HorseModal.js's registration checklist.
const TRACKED_ORGS = ['AQHA', 'APHA', 'NRHA', 'NRBC'];

const SectionCard = ({ title, xs = 12, md = 6, children }) => (
  <Grid item xs={xs} md={md}>
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={title}
        titleTypographyProps={{ variant: 'subtitle1', fontWeight: 700 }}
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </CardContent>
    </Card>
  </Grid>
);

const HorseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [locations, setLocations] = useState([]);
  const [pedigreeModalOpen, setPedigreeModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [breedingOwnerTypeFilter, setBreedingOwnerTypeFilter] = useState('owned');
  const [horseRegistrations, setHorseRegistrations] = useState([]);
  const { openHorseModal } = useHorseModal();
  const openRegistrationDialogRef = useRef(null);

  useEffect(() => {
    setBreedingOwnerTypeFilter('owned');
  }, [id]);

  const loadHorse = useCallback(async () => {
    try {
      const response = await horsesAPI.getById(id);
      setHorse(response.data);
    } catch (error) {
      console.error('Error loading horse:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadHorse();
  }, [loadHorse]);

  // Load this horse's registrations (for the org status row in the header)
  useEffect(() => {
    const loadRegistrations = async () => {
      try {
        const response = await horseRegistrationsAPI.getAll(id);
        setHorseRegistrations(toArray(response.data, 'horse registrations'));
      } catch (error) {
        console.error('Error loading horse registrations:', error);
      }
    };

    loadRegistrations();
  }, [id]);

  // Load reference data (locations for display)
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const locationsRes = await referenceAPI.getLocations();
        setLocations(toArray(locationsRes.data, 'locations'));
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };

    loadReferenceData();
  }, []);

  const displayDate = (value) => formatDate(value) ?? 'N/A';

  const formatCurrency = (value) => {
    if (value == null || value === '') return 'N/A';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'for_sale':
        return 'primary';
      case 'sale_pending':
        return 'warning';
      case 'retired':
        return 'secondary';
      case 'sold':
        return 'info';
      case 'deceased':
        return 'error';
      case 'lease_terminated':
        return 'warning';
      default:
        return 'default';
    }
  };

  const locationDisplay = useMemo(() => {
    if (!horse?.location) return 'N/A';
    const location = locations.find((loc) => loc.name === horse.location);
    if (!location) return horse.location;

    const extractBusiness = (value) => {
      if (!value) return null;
      const parts = value.split(' — ');
      return parts[parts.length - 1] || null;
    };

    const referenceParts = [
      extractBusiness(location.trainer_display),
      extractBusiness(location.vet_display),
    ].filter(Boolean);

    if (referenceParts.length === 0) {
      return location.name;
    }

    const uniqueParts = Array.from(new Set(referenceParts));
    return `${location.name} — ${uniqueParts.join(' • ')}`;
  }, [horse?.location, locations]);

  // Form handlers
  const handleOpenEditDialog = () => {
    if (horse) {
      openHorseModal(horse, () => {
        loadHorse();
      });
    }
  };

  if (loading) {
    return (
      <DetailPageSkeleton
        backLabel="Back to Horses"
        onBack={() => navigate('/horses')}
        heroSx={{ background: `linear-gradient(135deg, ${BRAND[800]} 0%, ${BRAND[600]} 100%)` }}
        dark
        label="Loading horse…"
      />
    );
  }

  if (!horse) {
    return (
      <Box>
        <Typography variant="h5">Horse not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/horses')}>
          Back to Horses
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/horses')}
        sx={{ mb: 2 }}
      >
        Back to Horses
      </Button>

      <Paper
        sx={{
          p: 3,
          mb: 3,
          boxShadow: cardSx.boxShadow,
          background: `linear-gradient(135deg, ${BRAND[800]} 0%, ${BRAND[600]} 100%)`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            rowGap: 1,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
            <Typography variant="h4" sx={{ color: '#fff' }}>{horse.name}</Typography>
            <IconButton
              onClick={handleOpenEditDialog}
              aria-label="edit horse"
              sx={{ width: 44, height: 44, color: '#fff' }}
            >
              <EditIcon />
            </IconButton>
            <Button
              variant="outlined"
              onClick={() => setPedigreeModalOpen(true)}
              sx={{
                ml: 1,
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
              }}
            >
              Pedigree
            </Button>
            {horse.notes ? (
              <Button
                variant="outlined"
                onClick={() => setNotesModalOpen(true)}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Notes
              </Button>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
              width: { xs: '100%', sm: 'auto' },
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
              {TRACKED_ORGS.map((orgName, index) => {
                const isTargeted = (horse.registration_targets || []).includes(orgName);
                const reg = horseRegistrations.find((r) => r.registration_org_name === orgName);
                const submittedDate = (horse.registration_submitted_dates || {})[orgName];
                const neutralColor = 'rgba(255,255,255,0.4)';
                // A registration on file wins over a submitted date. Amber is the
                // in-between state: paperwork sent, certificate not back yet.
                let statusColor = neutralColor;
                if (isTargeted) {
                  if (reg) statusColor = '#66bb6a';
                  else if (submittedDate) statusColor = '#ffa726';
                  else statusColor = '#ef5350';
                }
                return (
                  <React.Fragment key={orgName}>
                    {index === 2 && (
                      <Box sx={{ flexBasis: { xs: '100%', sm: 0 }, width: 0, height: 0 }} />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isTargeted ? (
                        <CheckBoxIcon sx={{ fontSize: 20, color: statusColor }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon sx={{ fontSize: 20, color: neutralColor }} />
                      )}
                      <FiberManualRecordIcon sx={{ fontSize: 8, color: statusColor }} />
                      <Typography
                        variant="body2"
                        sx={{ color: isTargeted ? '#fff' : neutralColor, fontWeight: 700 }}
                      >
                        {orgName}
                      </Typography>
                      {reg && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                          {reg.registration_number}
                        </Typography>
                      )}
                      {!reg && isTargeted && submittedDate && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                          Submitted {displayDate(submittedDate)}
                        </Typography>
                      )}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
            <HorseTypeBadge type={horse.type} />
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <SectionCard title="Basic Info">
          <Field label="Registered Name">{horse.registered_name || 'N/A'}</Field>
          <Field label="Breed">{horse.breed || 'N/A'}</Field>
          <Field label="Color">{horse.color || 'N/A'}</Field>
          <Field label="Date of Birth">{displayDate(horse.date_of_birth)}</Field>
          <Field label="Status">
            {horse.current_status ? (
              <Chip
                size="small"
                label={formatHorseCurrentStatus(horse.current_status)}
                color={getStatusColor(horse.current_status)}
              />
            ) : (
              'N/A'
            )}
          </Field>
          <Field label="Location">{locationDisplay}</Field>
          <Field label="Blanket Size">{horse.blanket_size || 'N/A'}</Field>
        </SectionCard>

        <SectionCard title="Pedigree">
          <Field label="Sire">
            {horse.sire_name || (horse.sire_horse ? (horse.sire_horse.name || horse.sire_horse.registered_name || `Horse #${horse.sire_horse.id}`) : 'N/A')}
          </Field>
          <Field label="Dam">
            {horse.dam_name || (horse.dam_horse ? (horse.dam_horse.name || horse.dam_horse.registered_name || `Horse #${horse.dam_horse.id}`) : 'N/A')}
          </Field>
          <Field label="Sire's Sire">{horse.sire_sire_name || 'N/A'}</Field>
          <Field label="Dam's Sire">{horse.dam_sire_name || 'N/A'}</Field>
        </SectionCard>

        <SectionCard title="Genetics & Health">
          <Field label="Color Panel">{horse.color_panel || 'N/A'}</Field>
          <Field label="DNA Test">{horse.dna_test || 'N/A'}</Field>
          <Field label="Disease Panel">{horse.disease_panel || 'N/A'}</Field>
        </SectionCard>

        <SectionCard title="Financial">
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Field label="Date Purchased" sm={12}>{displayDate(horse.date_purchased)}</Field>
              <Field label="Date Purchase Paid" sm={12}>{displayDate(horse.date_purchase_paid)}</Field>
              <Field label="Purchase Amount" sm={12}>{formatCurrency(horse.purchase_amount)}</Field>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Grid container spacing={2}>
              <Field label="Date Sold" sm={12}>{displayDate(horse.date_sold)}</Field>
              <Field label="Date Sale Paid" sm={12}>{displayDate(horse.date_sale_paid)}</Field>
              <Field label="Sale Amount" sm={12}>{formatCurrency(horse.sale_amount)}</Field>
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="AllBreed Registration" md={12}>
          <Field label="AllBreed ID">
            {horse.allbreed_horse_id
              ? <a href={`https://beta.allbreedpedigree.com/${horse.allbreed_horse_id}`} target="_blank" rel="noreferrer">{horse.allbreed_horse_id}</a>
              : 'N/A'}
          </Field>
          <Field label="AllBreed Status">
            <span style={{ textTransform: 'capitalize' }}>{horse.allbreed_status || 'N/A'}</span>
          </Field>
          <Field label="AllBreed Submitted">
            {horse.allbreed_submitted_at ? displayDate(horse.allbreed_submitted_at) : 'N/A'}
          </Field>
        </SectionCard>
      </Grid>

      <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            py: 1.5,
            backgroundColor: BRAND[50],
            borderBottom: `1px solid ${BRAND[100]}`,
            maxWidth: '100%',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minWidth: 0, maxWidth: '100%' }}
          >
            <Tab label="Registrations" />
            <Tab label="Breeding History" />
            <Tab label="Vet Records" />
            <Tab label="Feed Schedule" />
            <Tab label="Show Records" />
            <Tab label="Payments" />
          </Tabs>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {activeTab === 0 && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openRegistrationDialogRef.current && openRegistrationDialogRef.current()}
              >
                Add Registration
              </Button>
            )}
            {activeTab === 1 && horse.type?.toLowerCase() === 'stallion' && (
              <Autocomplete
                id="horse-detail-breeding-owner-type"
                size="small"
                disableClearable
                sx={{ minWidth: 150 }}
                options={BREEDING_OWNER_TYPE_OPTIONS}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(o, v) => o.value === v?.value}
                filterOptions={createPrefixFilter((o) => o.label)}
                value={
                  BREEDING_OWNER_TYPE_OPTIONS.find((o) => o.value === breedingOwnerTypeFilter)
                  ?? BREEDING_OWNER_TYPE_OPTIONS[0]
                }
                onChange={(e, newValue) => setBreedingOwnerTypeFilter(newValue?.value ?? '')}
                renderInput={(params) => <TextField {...params} label="Owner Type" />}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Fade in appear key={activeTab} timeout={200}>
            <Box>
              {activeTab === 0 && (
                <HorseRegistrations
                  horseId={horse.id}
                  onAddClick={(fn) => { openRegistrationDialogRef.current = fn; }}
                  onChange={setHorseRegistrations}
                />
              )}
              {activeTab === 1 && (
                <HorseBreedingHistory horseId={horse.id} ownerTypeFilter={breedingOwnerTypeFilter} />
              )}
              {activeTab === 2 && (
                <HorseVetRecords horseId={horse.id} />
              )}
              {activeTab === 3 && (
                <HorseFeedSchedule horseId={horse.id} />
              )}
              {activeTab === 4 && (
                <HorseShowRecords horseId={horse.id} />
              )}
              {activeTab === 5 && (
                <HorsePayments horseId={horse.id} horse={horse} onChange={loadHorse} />
              )}
            </Box>
          </Fade>
        </Box>
      </Paper>

      <PedigreeModal
        open={pedigreeModalOpen}
        onClose={() => setPedigreeModalOpen(false)}
        horseId={horse.id}
        horseName={horse.name || horse.registered_name}
      />
      <Dialog
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Notes</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {horse.notes}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HorseDetailPage;

